import * as React from 'react';
import { enrollForNewsletter } from '@packages/services';
import { Subject, isValidEmail, REGISTERED_SHA256 } from '@packages/common';
import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';
import { useTranslations } from '@packages/i18n';
import { NewsletterFooter, NewsletterFooterProps } from '../../modules/NewsletterFooter';
import { SideEffectsContext } from '../../context/SideEffectsContext/SideEffectsContext';

const APP_TLD = getPublicRuntimeConfig('APP_TLD');
const ROUTES = getPublicRuntimeConfig('ROUTES');
const BRIDGE_URL = getPublicRuntimeConfig('BRIDGE_URL');

const newsletterSubmit$ = new Subject<string>('');
export const NewsletterFooterContainer: React.FC<{ showContentfulHeading?: boolean; tag?: string }> = ({
  showContentfulHeading = false,
  tag,
}) => {
  const { newsletterRegistered } = React.useContext(SideEffectsContext);
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [disableSubmitButton, setDisableSubmitButton] = React.useState(false);
  const [invalidEmailErrorMessage, generalErrorMessage, emailAlreadyRegisteredMessage, successText, successText2] =
    useTranslations([
      'newsletterFooterInvalidEmailErrorMessage',
      'newsletterFooterGeneralErrorMessage',
      'newsletterFooterEmailAlreadyRegisteredMessage',
      'newsletterFooterSubmitSuccessMessage',
      'newsletterFooterSubmitSuccessMessage2',
    ]) as string[];
  const [submitMessage, setSubmitMessage] = React.useState(successText);

  const submitCallback = React.useCallback(
    (email) => {
      if (!isValidEmail(email)) {
        setErrorMessage(invalidEmailErrorMessage);
        return;
      }

      setDisableSubmitButton(true);
      enrollForNewsletter(BRIDGE_URL, { email, tld: APP_TLD, tag })
        .then(({ email_sha256: sha256 }) => {
          setIsSubmitted(true);
          setErrorMessage('');

          if (sha256 === REGISTERED_SHA256) {
            setSubmitMessage(successText2);
          } else {
            newsletterSubmit$.next(sha256);
            newsletterRegistered();
            setSubmitMessage(successText);
          }
        })
        .catch((err: any) => {
          setIsSubmitted(false);
          if (err) {
            const { error_key: errorKey } = err;
            if (errorKey === 'contact_key') {
              setErrorMessage(invalidEmailErrorMessage);
            } else if (errorKey === 'id') {
              setErrorMessage(emailAlreadyRegisteredMessage);
            } else {
              setErrorMessage(generalErrorMessage);
            }
          }
        })
        .finally(() => {
          setDisableSubmitButton(false);
        });
    },
    [
      emailAlreadyRegisteredMessage,
      generalErrorMessage,
      invalidEmailErrorMessage,
      newsletterRegistered,
      successText,
      successText2,
      tag,
    ],
  );

  const data: NewsletterFooterProps = {
    disableSubmitButton,
    onSubmit: submitCallback,
    errorMessage,
    isSubmitted,
    submitMessage,
    invalidEmailErrorMessage: useTranslations('newsletterFooterInvalidEmailErrorMessage') as string,
    heading: useTranslations('newsletterFooterHeading') as string,
    mainText: useTranslations('newsletterFooterHeadline') as string,
    dataProtectionNote: useTranslations('newsletterFooterDataProtectionNote') as string,
    dataProtectionLinkUrl: ROUTES.dataProtection,
    dataProtectionLinkLabel: useTranslations('newsletterFooterDataProtectionLinkLabel') as string,
    signUpButtonLabel: useTranslations('newsletterFooterSignUpButtonLabel') as string,
    signUpButtonPlaceholder: useTranslations('newsletterFooterSignUpButtonPlaceholder') as string,
    showContentfulHeading,
    hightlightsMsg: useTranslations('newsletterFooterContentfulHighlights') as string,
    offersMsg: useTranslations('newsletterFooterContentfulOffers') as string,
    trendsMsg: useTranslations('newsletterFooterContentfulTrends') as string,
  };

  return <NewsletterFooter {...data}></NewsletterFooter>;
};

export const useNewsletterCallbacks = () => {
  return { newsletterSubmit$ };
};
